import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { AuthStatus } from '@jelly-dl/shared';
import { api, setUnauthorizedHandler } from '../api/client';

interface AuthContextValue {
  status: AuthStatus | null;
  loading: boolean;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue>(null as unknown as AuthContextValue);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      setStatus(await api.get<AuthStatus>('/api/auth/status'));
    } catch {
      setStatus(null);
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    await api.post('/api/auth/logout').catch(() => {});
    api.resetCsrf();
    await refresh();
  };

  useEffect(() => {
    void refresh();
    // When any API call returns 401, fall back to the login gate instead of spinning forever.
    setUnauthorizedHandler(() => setStatus((s) => (s ? { ...s, authenticated: false } : s)));
    return () => setUnauthorizedHandler(null);
  }, []);

  return (
    <AuthContext.Provider value={{ status, loading, refresh, logout }}>{children}</AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  return useContext(AuthContext);
}
